/**
 * AerialSearchAction - behavior tree action for aerial search behavior.
 */

import * as THREE from 'three';

import { AerialMovementComponent } from '../../../../../components/movement/AerialMovementComponent.js';
import { EngineClass } from '../../../../../systems/ClassRegistry.js';
import { BehaviorStatus } from '../../../BehaviorNode.js';
import { BehaviorAction, type BehaviorActionOptions } from '../../BehaviorAction.js';

import type { Actor } from '../../../../../actors/Actor.js';
import type { Blackboard } from '../../../blackboard/Blackboard.js';

export interface AerialSearchActionOptions extends BehaviorActionOptions {
  lastKnownPositionKey?: string;
  playerActorKey?: string;
  searchRadius?: number;
  searchHeight?: number;
  searchDuration?: number;
}

@EngineClass('AerialSearchAction')
export class AerialSearchAction extends BehaviorAction {
  private lastKnownPositionKey: string;
  private playerActorKey: string;
  private searchRadius: number;
  private searchHeight: number;
  private searchDuration: number;
  private movementComponent: AerialMovementComponent | null = null;
  private searchStartTime: number = 0;
  private lastPlayerPosition: THREE.Vector3 | null = null;

  constructor(options: AerialSearchActionOptions = {}) {
    super({
      name: 'AerialSearch',
      ...options
    });

    this.lastKnownPositionKey = options.lastKnownPositionKey ?? 'LastKnownPlayerPosition';
    this.playerActorKey = options.playerActorKey ?? 'PlayerActor';
    this.searchRadius = options.searchRadius ?? 15.0;
    this.searchHeight = options.searchHeight ?? 20.0;
    this.searchDuration = options.searchDuration ?? 10.0;
  }

  protected override onInitialize(blackboard: Blackboard): void {
    const owner = this.getOwner(blackboard);
    if (!owner) {
      return;
    }

    this.movementComponent = owner.getComponent(AerialMovementComponent);
    if (!this.movementComponent) {
      console.warn('AerialSearchAction: No AerialMovementComponent found on actor');
      return;
    }
  }

  protected override onEnter(blackboard: Blackboard): void {
    this.searchStartTime = Date.now() / 1000;

    // Update last known player position
    const playerActor = blackboard.getValue<Actor>(this.playerActorKey);
    if (playerActor) {
      const playerPosition = new THREE.Vector3();
      playerActor.rootComponent.getWorldPosition(playerPosition);
      this.lastPlayerPosition = playerPosition.clone();
      blackboard.setValue(this.lastKnownPositionKey, playerPosition);
      blackboard.setValue(`${this.lastKnownPositionKey}_Timestamp`, this.searchStartTime);
    }
  }

  protected override async onUpdate(blackboard: Blackboard, deltaTime: number): Promise<BehaviorStatus> {
    const owner = this.getOwner(blackboard);
    if (!owner || !this.movementComponent) {
      return BehaviorStatus.Failure;
    }

    // Check if we've been searching too long
    const currentTime = Date.now() / 1000;
    const elapsedTime = currentTime - this.searchStartTime;

    if (elapsedTime >= this.searchDuration) {
      return BehaviorStatus.Success; // Give up searching
    }

    // Get last known player position
    let searchCenter = blackboard.getValue<THREE.Vector3>(this.lastKnownPositionKey);
    if (!searchCenter && this.lastPlayerPosition) {
      searchCenter = this.lastPlayerPosition;
    }

    if (!searchCenter) {
      return BehaviorStatus.Failure;
    }

    // Make an educated guess about where the player might be
    const estimatedPosition = this.estimatePlayerPosition(blackboard, searchCenter, elapsedTime);

    // Set the estimated position for the movement component to search around
    this.movementComponent.setLastKnownPlayerPosition(estimatedPosition);

    return BehaviorStatus.Running;
  }

  protected override onExit(blackboard: Blackboard, status: BehaviorStatus): void {
    // Clear search behavior
    if (this.movementComponent) {
      this.movementComponent.clearTarget();
      this.movementComponent.setLastKnownPlayerPosition(null);
    }
  }

  private estimatePlayerPosition(blackboard: Blackboard, lastKnownPos: THREE.Vector3, elapsedTime: number): THREE.Vector3 {
    const owner = this.getOwner(blackboard);
    if (!owner) {
      return lastKnownPos;
    }

    // Get owner position for distance calculations
    const ownerPosition = new THREE.Vector3();
    owner.rootComponent.getWorldPosition(ownerPosition);

    // Basic estimation: assume player might have moved away from the aerial enemy
    const directionAwayFromEnemy = lastKnownPos.clone().sub(ownerPosition).normalize();

    // Estimate how far the player might have moved (assuming average movement speed)
    const estimatedPlayerSpeed = 10; // units per second
    const estimatedDistance = Math.min(elapsedTime * estimatedPlayerSpeed, this.searchRadius);

    // Add some randomness to the estimation
    const randomAngle = (Math.random() - 0.5) * Math.PI; // ±90 degrees
    const rotationMatrix = new THREE.Matrix4().makeRotationY(randomAngle);
    directionAwayFromEnemy.applyMatrix4(rotationMatrix);

    // Calculate estimated position
    const estimatedPosition = lastKnownPos.clone().add(
      directionAwayFromEnemy.multiplyScalar(estimatedDistance)
    );

    // Keep the search at ground level (player can't fly)
    estimatedPosition.y = 1;

    return estimatedPosition;
  }
}
